/**
 * Parts for your machine: the results page.
 *
 * Reached from any "Shop by machine" box with ?fit_machine=MAKE MODEL&fit_year=YYYY. Every part
 * for the machine is fetched from the store's own product data (Storefront API, 250 a page),
 * and from then on everything happens on the page: the year is settled against each part's real
 * fitment, the categories are counted, and filtering, searching, sorting and paging are instant.
 *
 * Why the page fetches everything rather than one page of 48: Shopify caps a filter's value list
 * at 100 and, on the search route, does not narrow the counts to the machine at all. A Sportsman
 * 850 has more than 100 categories of parts, so the category list had to be counted here to be
 * complete and to be counted for the shopper's year.
 *
 * Nothing here claims more than the catalogue says. A card can say a part fits their machine and
 * year, or name the versions it was established for, or say to check - it never says a part does
 * NOT fit, because a gap in the data is not a known incompatibility. Parts the catalogue lists
 * for the machine but not for the year are kept out of the way, counted, and one click away.
 */
(function () {
  'use strict';

  var MACHINE_PARAM = 'fit_machine';
  var YEAR_PARAM = 'fit_year';
  var UNIVERSAL_PARAM = 'fit_universal';
  var GARAGE_KEY = 'ghs:garage';
  var PAGE_SIZE = 250;
  var MAX_PAGES = 8;
  var SHOW_AT_ONCE = 48;
  var UNIVERSAL = 'UNIVERSAL';
  /** How an accessory filed under a range is found: "RANGE POLARIS SPORTSMAN". */
  var RANGE_PREFIX = 'RANGE ';

  /* ------------------------------------------------------- fitment reading ---- */

  /**
   * The matching itself lives in fitment-match.js, shared with the product finder and the product
   * page badge, so the three cannot drift into answering the same question differently. This page
   * keeps what is its own: which products to ask for, and how to word the answer on a card.
   */
  var F = window.GHSFitment;

  /* ------------------------------------------------------------- helpers ---- */

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  function titleCase(text) {
    return String(text).toLowerCase().replace(/(^|[\s\/\-(])([a-z])/g, function (m, p, c) { return p + c.toUpperCase(); });
  }

  function money(amount, currency) {
    var n = Number(amount);
    if (isNaN(n)) return '';
    try { return n.toLocaleString(undefined, { style: 'currency', currency: currency || 'CAD' }); } catch (e) { return '$' + n.toFixed(2); }
  }

  function readGarage() {
    try { var raw = window.localStorage.getItem(GARAGE_KEY); return raw ? JSON.parse(raw) : null; } catch (e) { return null; }
  }
  function writeGarage(machine) {
    try { window.localStorage.setItem(GARAGE_KEY, JSON.stringify(machine)); } catch (e) {}
  }

  /* ---------------------------------------------------------- the page ---- */

  function Results(root) {
    this.root = root;
    this.token = root.getAttribute('data-storefront-token') || '';
    this.machinesUrl = root.getAttribute('data-machines-url');
    this.gammaBase = root.getAttribute('data-api-base') || '';
    this.searchUrl = root.getAttribute('data-search-url') || '/search';
    this.el = {
      title: root.querySelector('[data-mr-title]'),
      summary: root.querySelector('[data-mr-summary]'),
      status: root.querySelector('[data-mr-status]'),
      categories: root.querySelector('[data-mr-categories]'),
      brands: root.querySelector('[data-mr-brands]'),
      loading: root.querySelector('[data-mr-loading]'),
      loadingText: root.querySelector('[data-mr-loading-text]'),
      grid: root.querySelector('[data-mr-grid]'),
      more: root.querySelector('[data-mr-more]'),
      count: root.querySelector('[data-mr-count]'),
      search: root.querySelector('[data-mr-search]'),
      sort: root.querySelector('[data-mr-sort]'),
      stock: root.querySelector('[data-mr-stock]'),
      otherYears: root.querySelector('[data-mr-other-years]'),
      universal: root.querySelector('[data-mr-universal]'),
      clearFilters: root.querySelector('[data-mr-clear-filters]'),
      change: root.querySelector('[data-mr-change]'),
      filtersToggle: root.querySelector('[data-mr-filters-toggle]'),
      sidebar: root.querySelector('[data-mr-sidebar]')
    };

    var params = new URLSearchParams(window.location.search);
    this.label = (params.get(MACHINE_PARAM) || '').toUpperCase().replace(/\s+/g, ' ').trim();
    this.year = parseInt(params.get(YEAR_PARAM) || '', 10) || null;
    this.includeUniversal = params.get(UNIVERSAL_PARAM) === '1';
    this.filters = {
      cats: params.getAll('cat').map(function (c) { return c.toUpperCase(); }),
      brands: params.getAll('brand'),
      stock: params.get('stock') === '1',
      q: params.get('fit_q') || '',
      sort: params.get('sort') || 'best',
      otherYears: params.get('years') === 'all'
    };

    this.items = [];          // every part for the machine, after the year verdict
    this.hiddenOtherYears = 0;
    this.shown = 0;
    this.lazyQueue = [];
    this.lazyActive = 0;
    this.observer = null;

    if (!this.label) { this.root.hidden = true; return; }
    document.documentElement.classList.add('mr-active');
    this.bind();
    this.start();
  }

  Results.prototype.bind = function () {
    var self = this;
    if (this.el.search) {
      var timer = null;
      this.el.search.value = this.filters.q;
      this.el.search.addEventListener('input', function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(function () { self.filters.q = self.el.search.value.trim(); self.refresh(); }, 200);
      });
    }
    if (this.el.sort) {
      this.el.sort.value = this.filters.sort;
      this.el.sort.addEventListener('change', function () { self.filters.sort = self.el.sort.value; self.refresh(); });
    }
    if (this.el.stock) {
      this.el.stock.checked = this.filters.stock;
      this.el.stock.addEventListener('change', function () { self.filters.stock = self.el.stock.checked; self.refresh(); });
    }
    if (this.el.otherYears) {
      this.el.otherYears.addEventListener('click', function (e) { e.preventDefault(); self.filters.otherYears = !self.filters.otherYears; self.refresh(); });
    }
    if (this.el.universal) {
      this.el.universal.checked = this.includeUniversal;
      this.el.universal.addEventListener('change', function () {
        self.includeUniversal = self.el.universal.checked;
        var params = new URLSearchParams(window.location.search);
        if (self.includeUniversal) params.set(UNIVERSAL_PARAM, '1'); else params.delete(UNIVERSAL_PARAM);
        window.location.search = params.toString();
      });
    }
    if (this.el.clearFilters) {
      this.el.clearFilters.addEventListener('click', function (e) {
        e.preventDefault();
        self.filters.cats = []; self.filters.brands = []; self.filters.stock = false; self.filters.q = '';
        if (self.el.search) self.el.search.value = '';
        if (self.el.stock) self.el.stock.checked = false;
        self.refresh();
      });
    }
    if (this.el.more) {
      this.el.more.addEventListener('click', function () { self.renderMore(); });
    }
    if (this.el.filtersToggle && this.el.sidebar) {
      this.el.filtersToggle.addEventListener('click', function () {
        var open = self.el.sidebar.classList.toggle('is-open');
        self.el.filtersToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    if (this.el.change) {
      this.el.change.href = this.searchUrl;
    }
  };

  Results.prototype.setStatus = function (text) {
    if (this.el.status) { this.el.status.textContent = text || ''; this.el.status.hidden = !text; }
  };

  /**
   * The spinner, and what it says.
   *
   * It is on screen exactly as long as the page is really fetching, and the words carry the
   * running count rather than a guess - a machine with 300 parts takes more than one request,
   * and the shopper can watch the number climb. Stand-in cards were tried here and they lie:
   * they draw a page that does not exist yet, in a shape and a number that owe nothing to what
   * is actually coming back.
   */
  Results.prototype.setLoading = function (on, text) {
    var box = this.el.loading;
    if (!box) return;
    box.hidden = !on;
    if (on && text && this.el.loadingText) this.el.loadingText.textContent = text;
  };

  /** Loads the machine index, then every part; renders after the first page. */
  Results.prototype.start = function () {
    var self = this;
    this.setLoading(true, 'Looking for parts that fit your machine...');
    fetch(this.machinesUrl)
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; })
      .then(function (index) {
        self.resolveMachine(index);
        return self.fetchAll();
      })
      .catch(function () {
        self.setLoading(false);
        self.setStatus('Could not load the parts list. Please refresh and try again.');
      });
  };

  /**
   * "POLARIS SPORTSMAN 850" carries no separator, so the make is the longest known make the label
   * starts with. The group behind the name - every spelling the catalogue files parts under - is
   * what the badge reads the versions from.
   */
  Results.prototype.resolveMachine = function (index) {
    var makes = {};
    var groups = (index && index.groups) || {};
    if (index && index.types) {
      Object.keys(index.types).forEach(function (type) {
        Object.keys(index.types[type]).forEach(function (make) { makes[make] = true; });
      });
    }
    var make = null;
    Object.keys(makes).forEach(function (candidate) {
      if (this.label === candidate || this.label.indexOf(candidate + ' ') === 0) {
        if (!make || candidate.length > make.length) make = candidate;
      }
    }, this);
    this.make = make || this.label.split(' ')[0];
    this.model = this.label.slice(this.make.length).trim();
    var group = groups[this.make] && groups[this.make][this.model];
    this.members = group || null;
    if (!group && groups[this.make]) {
      // They arrived on one spelling; the machine it belongs to names the versions on the badge.
      Object.keys(groups[this.make]).some(function (base) {
        if (groups[this.make][base].indexOf(this.model) !== -1) { this.base = base; this.members = groups[this.make][base]; return true; }
        return false;
      }, this);
    }
    this.base = this.base || this.model;
    /**
     * The range their machine belongs to - SPORTSMAN for a SPORTSMAN 570 - where accessories are
     * filed with no engine named. Asked for beside the machine, and badged as the range, never as
     * their machine. Parts filed that way carry no value to ask for. See fitment-match.js.
     */
    var ranges = (index && index.ranges && index.ranges[this.make]) || {};
    this.range = ranges[this.base] || ranges[this.model] || null;
    writeGarage({ make: this.make, model: this.model, year: this.year });

    var name = this.make + ' ' + this.model;
    if (this.el.title) this.el.title.textContent = 'Parts for your ' + (this.year ? this.year + ' ' : '') + name;
    var suffix = document.title.split(/ [–-] /).pop();
    document.title = 'Parts for ' + (this.year ? this.year + ' ' : '') + name + (suffix ? ' – ' + suffix : '');
  };

  var QUERY = [
    'query($q: String!, $filters: [ProductFilter!], $after: String) {',
    '  search(query: $q, types: PRODUCT, first: ' + PAGE_SIZE + ', after: $after, productFilters: $filters) {',
    '    totalCount',
    '    pageInfo { hasNextPage endCursor }',
    '    edges { node { ... on Product {',
    '      id title handle productType vendor',
    '      featuredImage { url(transform: { maxWidth: 480 }) }',
    '      variants(first: 10) { edges { node {',
    '        id sku availableForSale quantityAvailable',
    '        price { amount currencyCode }',
    '        image { url(transform: { maxWidth: 480 }) }',
    '        metafield(namespace: "fitment", key: "machines") { value }',
    '      } } }',
    '    } } }',
    '  }',
    '}'
  ].join('\n');

  Results.prototype.fetchAll = function () {
    var self = this;
    var filters = [{ productMetafield: { namespace: 'fitment', key: 'machine', value: this.label } }];
    if (this.includeUniversal) filters.push({ productMetafield: { namespace: 'fitment', key: 'machine', value: UNIVERSAL } });
    if (this.range) filters.push({ productMetafield: { namespace: 'fitment', key: 'machine', value: RANGE_PREFIX + this.make + ' ' + this.range } });
    var after = null;
    var page = 0;

    function next() {
      return fetch('/api/2025-01/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': self.token },
        body: JSON.stringify({ query: QUERY, variables: { q: '*', filters: filters, after: after } })
      })
        .then(function (r) { return r.json(); })
        .then(function (json) {
          var search = json && json.data && json.data.search;
          if (!search) throw new Error('no results');
          (search.edges || []).forEach(function (edge) { self.adopt(edge.node); });
          page += 1;
          self.refresh();
          if (search.pageInfo && search.pageInfo.hasNextPage && page < MAX_PAGES) {
            after = search.pageInfo.endCursor;
            self.setLoading(true, self.items.length.toLocaleString() + ' parts so far...');
            return next();
          }
          self.setLoading(false);
          self.loaded = true;
          self.refresh();
        });
    }
    return next();
  };

  /** One card per product: the best-fitting variant speaks for it. */
  Results.prototype.adopt = function (product) {
    var variants = ((product.variants && product.variants.edges) || []).map(function (e) { return e.node; });
    var fitmentJson = variants.map(function (v) { return v.metafield && v.metafield.value; }).filter(Boolean);
    var verdict = F.yearVerdict(fitmentJson, this.make, this.model, this.year, this.members);
    if (verdict.verdict === 'other-machine') return;

    var variant = variants.filter(function (v) { return v.sku; })[0] || variants[0];
    if (!variant) return;
    var badge = this.badgeFor(verdict);
    var catKey = String(product.productType || 'Other parts').trim().toUpperCase() || 'OTHER PARTS';
    this.items.push({
      id: product.id,
      title: product.title,
      handle: product.handle,
      url: '/products/' + product.handle + '?variant=' + variant.id.split('/').pop(),
      sku: variant.sku || '',
      vendor: product.vendor || '',
      catKey: catKey,
      category: titleCase(catKey),
      image: (variant.image && variant.image.url) || (product.featuredImage && product.featuredImage.url) || '',
      price: variant.price ? money(variant.price.amount, variant.price.currencyCode) : '',
      priceValue: variant.price ? Number(variant.price.amount) : 0,
      available: Boolean(variant.availableForSale),
      quantity: variant.quantityAvailable,
      verdict: verdict.verdict,
      badge: badge,
      searchText: (product.title + ' ' + (variant.sku || '') + ' ' + catKey + ' ' + (product.vendor || '')).toUpperCase()
    });
  };

  Results.prototype.badgeFor = function (verdict) {
    var names = F.matchedTrims(this.base, this.members, verdict.matched);
    var trim = names ? F.trimList(names) : null;
    switch (verdict.verdict) {
      case 'fits':
      case 'no-year':
        return trim ? { state: 'partial', text: 'Fits ' + trim + ' only' } : { state: 'fits', text: this.make ? 'Fits your ' + this.make : 'Fits your machine' };
      case 'trim':
        return { state: 'partial', text: trim ? 'Fits ' + trim + ' only' : 'Fits some versions - check yours' };
      case 'wrong-year':
        return { state: 'other', text: verdict.years.length ? 'Fits ' + F.formatRanges(verdict.years) : 'Other years' };
      case 'range':
        return { state: 'partial', text: 'Fits the ' + titleCase(this.make + ' ' + (this.range || '')) + ' range - check your model' };
      default:
        return { state: 'check', text: 'Check fitment' };
    }
  };

  /* --------------------------------------------------------- filtering ---- */

  /** The parts that count for the current year setting; the category counts are taken from these. */
  Results.prototype.inYear = function () {
    var showOther = this.filters.otherYears;
    var other = 0;
    var kept = this.items.filter(function (item) {
      if (item.verdict === 'wrong-year') { other += 1; return showOther; }
      return true;
    });
    this.hiddenOtherYears = other;
    return kept;
  };

  Results.prototype.visible = function () {
    var f = this.filters;
    var words = f.q ? f.q.toUpperCase().split(/\s+/).filter(Boolean) : [];
    var list = this.inYear().filter(function (item) {
      if (f.cats.length && f.cats.indexOf(item.catKey) === -1) return false;
      if (f.brands.length && f.brands.indexOf(item.vendor) === -1) return false;
      if (f.stock && !item.available) return false;
      if (words.length && !words.every(function (w) { return item.searchText.indexOf(w) !== -1; })) return false;
      return true;
    });
    var RANK = { fits: 0, partial: 1, check: 2, other: 3 };
    var LAST = { 'OTHER PARTS': 1 };
    var sort = f.sort;
    list.sort(function (a, b) {
      if (sort === 'price-asc') return a.priceValue - b.priceValue || a.title.localeCompare(b.title);
      if (sort === 'price-desc') return b.priceValue - a.priceValue || a.title.localeCompare(b.title);
      if (sort === 'name') return a.title.localeCompare(b.title);
      // best match: the parts arrive in category order, so a shopper reads one kind of part at a
      // time. Inside a category the confirmed fits lead, then whatever is in stock, then by name.
      // Parts with no category of their own go last rather than sitting under O for "Other".
      return (LAST[a.catKey] || 0) - (LAST[b.catKey] || 0) || a.category.localeCompare(b.category) ||
        (RANK[a.badge.state] - RANK[b.badge.state]) || (Number(b.available) - Number(a.available)) ||
        a.title.localeCompare(b.title);
    });
    return list;
  };

  Results.prototype.refresh = function () {
    this.pushUrl();
    this.renderFacets();
    this.list = this.visible();
    this.shown = 0;
    this.el.grid.innerHTML = '';
    this.renderMore();
    this.renderSummary();
  };

  Results.prototype.pushUrl = function () {
    var params = new URLSearchParams(window.location.search);
    ['cat', 'brand', 'stock', 'fit_q', 'sort', 'years'].forEach(function (k) { params.delete(k); });
    this.filters.cats.forEach(function (c) { params.append('cat', c); });
    this.filters.brands.forEach(function (b) { params.append('brand', b); });
    if (this.filters.stock) params.set('stock', '1');
    if (this.filters.q) params.set('fit_q', this.filters.q);
    if (this.filters.sort !== 'best') params.set('sort', this.filters.sort);
    if (this.filters.otherYears) params.set('years', 'all');
    var url = window.location.pathname + '?' + params.toString();
    if (window.history && window.history.replaceState) window.history.replaceState(null, '', url);
  };

  /**
   * Category and brand lists with counts, counted for the shopper's year and whatever else is
   * ticked, so a number beside a category is always the number they will get by ticking it.
   */
  Results.prototype.renderFacets = function () {
    var self = this;
    var f = this.filters;
    var words = f.q ? f.q.toUpperCase().split(/\s+/).filter(Boolean) : [];
    var base = this.inYear().filter(function (item) {
      if (f.stock && !item.available) return false;
      if (words.length && !words.every(function (w) { return item.searchText.indexOf(w) !== -1; })) return false;
      return true;
    });

    /**
     * Alphabetical, not biggest-first. A shopper reading a filter list is looking for a word
     * they already have in mind - "brakes", "Yamaha" - and A to Z is the only order they can
     * predict, so it is the only one they can stop reading early.
     */
    function count(list, key) {
      var counts = {};
      list.forEach(function (item) { var k = item[key]; if (!k) return; counts[k] = (counts[k] || 0) + 1; });
      return Object.keys(counts).map(function (k) { return { key: k, count: counts[k] }; }).sort(function (a, b) { return a.key.localeCompare(b.key); });
    }


    // Categories are counted with the brand filter applied and vice versa, so the two lists agree.
    var cats = count(base.filter(function (i) { return !f.brands.length || f.brands.indexOf(i.vendor) !== -1; }), 'catKey');
    var brands = count(base.filter(function (i) { return !f.cats.length || f.cats.indexOf(i.catKey) !== -1; }), 'vendor');

    if (this.el.categories) {
      this.el.categories.innerHTML = cats.map(function (c) {
        var checked = f.cats.indexOf(c.key) !== -1;
        return '<label class="mr-check"><input type="checkbox" value="' + escapeHtml(c.key) + '"' + (checked ? ' checked' : '') + '><span>' + escapeHtml(titleCase(c.key)) + '</span><b>' + c.count + '</b></label>';
      }).join('') || '<p class="mr-facet-empty">No categories yet</p>';
      this.el.categories.querySelectorAll('input').forEach(function (input) {
        input.addEventListener('change', function () { self.toggle('cats', input.value, input.checked); });
      });
    }
    if (this.el.brands) {
      this.el.brands.innerHTML = brands.map(function (b) {
        var checked = f.brands.indexOf(b.key) !== -1;
        return '<label class="mr-check"><input type="checkbox" value="' + escapeHtml(b.key) + '"' + (checked ? ' checked' : '') + '><span>' + escapeHtml(b.key) + '</span><b>' + b.count + '</b></label>';
      }).join('');
      this.el.brands.querySelectorAll('input').forEach(function (input) {
        input.addEventListener('change', function () { self.toggle('brands', input.value, input.checked); });
      });
    }
    var active = f.cats.length + f.brands.length + (f.stock ? 1 : 0) + (f.q ? 1 : 0);
    if (this.el.clearFilters) this.el.clearFilters.hidden = active === 0;
    if (this.el.filtersToggle) this.el.filtersToggle.textContent = active ? 'Filters (' + active + ')' : 'Filters';
  };

  Results.prototype.toggle = function (which, value, on) {
    var list = this.filters[which];
    var at = list.indexOf(value);
    if (on && at === -1) list.push(value);
    if (!on && at !== -1) list.splice(at, 1);
    this.refresh();
  };

  Results.prototype.renderSummary = function () {
    var total = this.list.length;
    var text;
    if (!this.loaded && !this.items.length) text = '';
    else if (total === 0) text = 'No parts match. Try clearing a filter.';
    else text = total + (total === 1 ? ' part' : ' parts') + (this.filters.cats.length || this.filters.brands.length || this.filters.q || this.filters.stock ? ' match' : ' fit');
    if (this.el.count) this.el.count.textContent = text;

    if (this.el.otherYears) {
      var other = this.hiddenOtherYears;
      if (other > 0 && this.year) {
        this.el.otherYears.hidden = false;
        this.el.otherYears.textContent = this.filters.otherYears
          ? 'Hide the ' + other + ' parts that fit this machine in other years'
          : other + ' more ' + (other === 1 ? 'part fits' : 'parts fit') + ' this machine in other years - show them';
      } else {
        this.el.otherYears.hidden = true;
      }
    }
  };

  /* --------------------------------------------------------- rendering ---- */

  Results.prototype.renderMore = function () {
    var slice = this.list.slice(this.shown, this.shown + SHOW_AT_ONCE);
    var html = slice.map(this.card, this).join('');
    this.el.grid.insertAdjacentHTML('beforeend', html);
    this.shown += slice.length;
    var left = this.list.length - this.shown;
    if (this.el.more) {
      this.el.more.hidden = left <= 0;
      this.el.more.textContent = 'Show ' + Math.min(SHOW_AT_ONCE, left) + ' more (' + left + ' left)';
    }
    this.watchLazy();
  };

  Results.prototype.card = function (item) {
    var stock = item.available
      ? '<span class="mr-stock in">In stock' + (item.quantity > 0 ? ' · ' + item.quantity : '') + '</span>'
      : '<span class="mr-stock out">Out of stock</span><span class="mr-eta" data-mr-eta>checking arrival...</span>';
    return '<a class="mr-card mr-badge-' + item.badge.state + '" href="' + escapeHtml(item.url) + '"' + (item.available ? '' : ' data-mr-lazy="' + escapeHtml(item.sku) + '"') + '>' +
      '<div class="mr-media">' + (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="" loading="lazy">' : '<div class="mr-noimage">No photo</div>') +
      '<span class="mr-badge">' + escapeHtml(item.badge.text) + '</span></div>' +
      '<div class="mr-body"><div class="mr-cat">' + escapeHtml(item.category) + '</div>' +
      '<div class="mr-title">' + escapeHtml(item.title) + '</div>' +
      '<div class="mr-sku">' + escapeHtml(item.vendor ? item.vendor + ' · ' : '') + escapeHtml(item.sku) + '</div>' +
      '<div class="mr-foot"><span class="mr-price">' + escapeHtml(item.price) + '</span>' + stock + '</div></div></a>';
  };

  /* ---------------------------------------------- arrival dates, on scroll ---- */

  Results.prototype.watchLazy = function () {
    var self = this;
    var cards = this.el.grid.querySelectorAll('[data-mr-lazy]');
    if (!this.gammaBase || !('IntersectionObserver' in window)) { cards.forEach(function (c) { self.enqueue(c); }); return; }
    if (!this.observer) {
      this.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          self.observer.unobserve(entry.target);
          self.enqueue(entry.target);
        });
      }, { rootMargin: '300px 0px' });
    }
    cards.forEach(function (card) { self.observer.observe(card); });
  };

  Results.prototype.enqueue = function (card) {
    var sku = card.getAttribute('data-mr-lazy');
    if (!sku) return;
    card.removeAttribute('data-mr-lazy');
    this.lazyQueue.push({ card: card, sku: sku });
    this.pump();
  };

  Results.prototype.pump = function () {
    var self = this;
    while (this.lazyActive < 2 && this.lazyQueue.length) {
      var job = this.lazyQueue.shift();
      this.lazyActive += 1;
      this.arrival(job).catch(function () {}).then(function () { self.lazyActive -= 1; self.pump(); });
    }
  };

  Results.prototype.arrival = function (job) {
    var slot = job.card.querySelector('[data-mr-eta]');
    if (!slot || !this.gammaBase) return Promise.resolve();
    var url = this.gammaBase + '/inventory/getEstimatedArrivalsForItem?partNumber=' + encodeURIComponent(job.sku);
    return fetch(url).then(function (r) { return r.ok ? r.json() : null; }).then(function (json) {
      var dates = json && json.data && json.data.arrivalDates;
      if (!Array.isArray(dates) || !dates.length) { slot.textContent = 'No arrival date yet'; return; }
      dates.sort(function (a, b) { return new Date(a.eta) - new Date(b.eta); });
      var when = new Date(dates[0].eta).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      slot.textContent = (dates[0].etaType === 'CARGO' ? 'Arriving ' : 'Expected ') + when;
    }).catch(function () { slot.textContent = 'No arrival date yet'; });
  };

  function init() {
    document.querySelectorAll('[data-machine-results]').forEach(function (root) {
      if (root.dataset.mrReady) return;
      root.dataset.mrReady = '1';
      new Results(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
